import { Box, FormControl, MenuItem, Select, TextField } from "@mui/material";
import { memo, useMemo, useState } from "react";
import CountryFlag from "react-country-flag";
import { Control, Controller, FieldErrors, UseFormSetValue } from "react-hook-form";
import countries from "../../data/countriesList.json";
import { menuPropsModal } from "../../utils";
import { SASInputStyles } from "../ui/styles/inputStyles";

interface Props<T = any> {
  control: Control<T>;
  errors: FieldErrors<T>;
  setValue: UseFormSetValue<T>;
  name: keyof T;
  countryCodeName: keyof T;
  required?: boolean;
  compact?: boolean;
}

/* ---------- Phone formatter (USA) ---------- */
const formatUSPhone = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 10);

  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const PhoneInputWithFlags = memo(
  ({
    control,
    errors,
    setValue,
    name = "phone",
    countryCodeName = "countryCode",
    required = true,
    compact = false,
  }: Props) => {
    const [isOpen, setIsOpen] = useState(false);

     const countryOptions = useMemo(
      () =>
        countries.map((c) => (
          <MenuItem key={c.code} value={c.code}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CountryFlag svg countryCode={c.code} style={{ width: 20 }} />
              {isOpen ? `${c.name} (${c.dialCode})` : c.dialCode}
            </Box>
          </MenuItem>
        )),
      [isOpen]
    );

    const countryCodeRules = required
      ? ({ required: "Country code is required" } as const)
      : undefined;

    const phoneRules = required
      ? ({
          required: "Phone number is required",
          minLength: { value: 10, message: "At least 10 digits" },
          maxLength: { value: 14, message: "Max 14 digits" },
        } as const)
      : undefined;

    const phoneError = (errors as FieldErrors<any>)?.[name as any];
    const phoneHelperText =
      typeof phoneError?.message === "string" ? phoneError.message : undefined;

    return (
      <Box sx={{ display: "flex", width: "100%", gap: 1, alignItems: "center" }}>
        {/* ---------- Country Code ---------- */}
        <Controller
          name={countryCodeName as any}
          control={control}
          defaultValue={"US" as any}
          rules={countryCodeRules as any}
          render={({ field }) => (
            <FormControl sx={{ minWidth: 90, ...(SASInputStyles as any) }}>
              <Select
                {...field}
                MenuProps={menuPropsModal}
                size="small"
                onOpen={() => setIsOpen(true)}
                onClose={() => setIsOpen(false)}
                onChange={(e) => {
                  const selectedCode = e.target.value;
                  field.onChange(selectedCode);

                  const country = countries.find((c) => c.code === selectedCode);
                  if (country) {
                    setValue("dialCode" as any, country.dialCode as any);
                  }
                }}
                sx={{
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    paddingY: compact ? "8px" : "12px",
                    fontSize: compact ? "0.9rem" : undefined,
                  },
                }}
                renderValue={(val) => {
                  const country = countries.find((c) => c.code === val);
                  return country ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CountryFlag
                        svg
                        countryCode={country.code}
                        style={{ width: 20 }}
                      />
                      {country.dialCode}
                    </Box>
                  ) : (
                    "Code"
                  );
                }}
              >
                {countryOptions}
              </Select>
            </FormControl>
          )}
        />

        {/* ---------- Phone ---------- */}
        <Controller
          name={name as any}
          control={control}
          rules={phoneRules as any}
          render={({ field }) => (
            <TextField
              label="Phone"
              variant="outlined"
              inputMode="tel"
              sx={{
                flex: 1,
                ...(SASInputStyles as any),
                ...(compact && {
                  "& input": {
                    padding: "8px 12px",
                    fontSize: "0.9rem",
                  },
                }),
              }}
              error={!!phoneError}
              helperText={phoneHelperText}
              value={(field.value as any) || ""}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, "");
                const limited = digits.slice(0, 10);
                const formatted = formatUSPhone(limited);
                field.onChange(formatted);
              }}
            />
          )}
        />
      </Box>
  );
});
